<script setup>
import Parallaxy from '@lucien144/vue3-parallaxy'

const route = useRoute()
const { data, error } = await useCmsFetch(`entry/${route.params.slug}`)
if (error.value) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Page Not Found',
    fatal: true,
  })
}

const { isMobileOrTablet } = useDevice()
const { width: innerWidth } = useWindowSize()

const isMobile = computed(() => {
  if (process.server) return isMobileOrTablet
  return innerWidth.value < 900
})

// Function to format date in ISO format for JSON-LD
// const formatDateISO = (date) => {
//   const d = new Date(date);
//   return d.toISOString();
// };

// Schema setup
if (data.value) {
  useSchemaOrg({
    '@context': 'https://schema.org',
    '@type': 'Article',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': window?.location.href,
    },
    headline: data.value.title,
    image: data.value.media ? data.value.media.url : '', // Ensure you replace this with the correct path to the image URL
    datePublished: formatDate(data.value.meta.postDate),
    author: {
      '@type': 'Person',
      name: data.value.meta.author ? data.value.meta.author : 'Unknown', // Replace with actual author data
    },
    publisher: {
      '@type': 'Organization',
      name: 'Your Organization Name', // Replace with your organization name
      logo: {
        '@type': 'ImageObject',
        url: 'Your Logo URL', // Replace with the URL to your organization’s logo
      },
    },
  })
}

const mediaOrientation = computed(() => {
  if (!data.value.media) return 'portrait'
  const { width, height } = data.value.media.attributes
  if (width > height) return 'landscape'
  return 'portrait'
})
useHead({
  title: data.value?.seoTitle || data.value?.meta.title + ' | Vandstrom',
  meta: [
    {
      property: 'og:title',
      content: data.value?.seoTitle || data.value?.meta.title + ' | Vandstrom',
    },
    { name: 'description', content: data.value?.seoDescription },
    { name: 'robots', content: data.value?.seoRobots },
  ].filter((item) => item.content),
  link: [{ rel: 'canonical', href: data.value?.seoCanonical || useRequestURL().href }].filter(
    (item) => item.href,
  ),
})
</script>

<template>
  <div class="relative z-[calc(var(--z-index-header)+1)] min-h-screen bg-white">
    <globals-header-article :cta="data?.cta" />
    <ui-call-to-action
      v-if="isMobile"
      class="sticky left-[var(--grid-bleed)] top-[90%] z-[calc(var(--z-index-header)+2)] h-spacing-05 !w-[var(--cols-9)] items-center justify-center rounded-corner-02-desktop bg-focus-dark text-focus-light"
      :cta="data.cta"
      :is-article="true"
    ></ui-call-to-action>
    <ui-bleed>
      <div
        class="index flex flex-col gap-spacing-06 pb-spacing-09 pt-spacing-07 lg:gap-spacing-06-desktop lg:pb-spacing-06-desktop lg:pt-spacing-08-desktop"
        v-if="data"
      >
        <div
          class="article-heading flex flex-col gap-spacing-06 lg:ml-[var(--push-1)] lg:w-[var(--cols-10)] lg:gap-spacing-06-desktop"
        >
          <ui-font-text
            v-if="data.trumpet"
            type="body"
            class="ml-[var(--push-3)] text-dark-ivory lg:ml-[var(--push-5)]"
            >{{ data.trumpet }}</ui-font-text
          >
          <ui-font-text
            v-else
            type="body"
            class="ml-[var(--push-3)] text-dark-ivory lg:ml-[var(--push-5)]"
            >{{ formatDate(data.meta.postDate) }}</ui-font-text
          >
          <ui-font-text
            class="article-heading text-focus-dark lg:ml-[var(--push-5)] lg:w-[var(--cols-5)]"
            type="h2"
            >{{ data.title }}</ui-font-text
          >
        </div>
        <div
          class="article-content flex flex-col gap-spacing-06 lg:flex-row lg:gap-[var(--push-1)]"
        >
          <!-- <div
            v-if="data.media"
            class="article-media w-[var(--cols-8)] shrink-0 lg:w-[var(--cols-4)]"
          >
            <component-element-media
              :media="data.media"
              :is-rounded="true"
              class="aspect-portrait h-auto lg:sticky lg:top-[20%]"
            />
          </div> -->

          <Parallaxy
            v-if="data.media"
            :speed="40"
            direction="opposite"
            class="article-media h-full shrink-0 sm:relative lg:mb-spacing-06-desktop lg:ml-[var(--cols-1)] lg:w-[calc(var(--push-4)+var(--grid-gap))]"
            :class="{
              'aspect-portrait': mediaOrientation === 'portrait',
              'aspect-landscape': mediaOrientation === 'landscape',
            }"
          >
            <div
              class="w-full overflow-hidden rounded-corner-02 lg:rounded-corner-02-desktop"
              :class="{
                'aspect-portrait lg:ml-[var(--grid-gap)] lg:w-[calc(100%-var(--grid-gap)*2)]':
                  mediaOrientation === 'portrait',
                'aspect-landscape': mediaOrientation === 'landscape',
              }"
            >
              <Parallaxy
                :speed="50"
                direction="opposite"
                class="w-full overflow-hidden rounded-corner-02 lg:rounded-corner-02-desktop"
                :class="{
                  'aspect-portrait': mediaOrientation === 'portrait',
                  'aspect-landscape': mediaOrientation === 'landscape',
                }"
              >
                <component-element-media
                  ref="media"
                  class="media h-full"
                  :media="data.media"
                  :is-rounded="true"
                ></component-element-media>
              </Parallaxy>
            </div>
          </Parallaxy>
          <div class="article-text">
            <ui-rich-text :rte="data.richText" />
          </div>
        </div>
      </div>
    </ui-bleed>
  </div>
</template>
